import { Badge, Spinner, makeStyles, tokens } from '@coreai/fluentui-react';
import type { NodeExecutionStatus } from '@/types/monitoring';
import { ottoTokens } from '@/theme/ottoTokens';

const useStyles = makeStyles({
  spinnerMargin: {
    marginRight: tokens.spacingHorizontalXS,
  },
  cancelledBadge: {
    backgroundColor: ottoTokens.statusCancelled,
    color: tokens.colorNeutralForegroundOnBrand,
    borderTopColor: ottoTokens.statusCancelled,
    borderRightColor: ottoTokens.statusCancelled,
    borderBottomColor: ottoTokens.statusCancelled,
    borderLeftColor: ottoTokens.statusCancelled,
  },
});

type NodeStatusBadgeProps = {
  status: NodeExecutionStatus;
  size?: 'small' | 'medium' | 'large';
};

const getAppearance = (
  status: NodeExecutionStatus,
): 'success' | 'danger' | 'warning' | 'informative' | 'subtle' => {
  switch (status) {
    case 'succeeded':
      return 'success';
    case 'failed':
      return 'danger';
    case 'cancelled':
      return 'warning';
    case 'running':
      return 'informative';
    case 'pending':
    case 'skipped':
    default:
      return 'subtle';
  }
};

const getLabel = (status: NodeExecutionStatus): string => {
  switch (status) {
    case 'succeeded':
      return 'Succeeded';
    case 'failed':
      return 'Failed';
    case 'cancelled':
      return 'Cancelled';
    case 'running':
      return 'Running';
    case 'skipped':
      return 'Skipped';
    case 'pending':
      return 'Pending';
    default:
      return status;
  }
};

/**
 * Renders a per-node execution status pill. Mirrors `RunStatusBadge` so the
 * action-level pills in `LogsPanel` (Properties grid and tab-footer) share the
 * exact Fluent palette used by the run-level badge in the monitoring header
 * and the runs sidebar. Centralising here keeps all status visuals in one
 * place and avoids the marigold-vs-olive mismatch caused by ad-hoc spans.
 */
export const NodeStatusBadge = ({ status, size = 'medium' }: NodeStatusBadgeProps) => {
  const styles = useStyles();
  const label = getLabel(status);
  const color = getAppearance(status);

  if (status === 'running') {
    return (
      <Badge appearance="filled" color="informative" size={size} data-status={status}>
        <Spinner size="extra-tiny" className={styles.spinnerMargin} />
        Running
      </Badge>
    );
  }

  return (
    <Badge
      appearance="filled"
      color={color}
      size={size}
      data-status={status}
      className={status === 'cancelled' ? styles.cancelledBadge : undefined}
    >
      {label}
    </Badge>
  );
};
